import { useAppDispatch, useAppSelector } from "@/app/appStore";
import {
  addJourney,
  deleteJourney,
  Journey,
  selectActiveJourneys,
  selectAnnotationData,
  selectAnnotationView,
  selectEvents,
  selectIsEditable,
  selectIsOrderLoading,
  selectJourneyProximities,
  selectJourneyTransactions,
  selectTrackPersistences,
  setSelectedEvent,
  setSelectedJourneyId,
} from "@/entities/annotation";
import {
  ANNOTATION_TYPES,
  ANNOTATION_RECORD_STATUSES,
} from "@/entities/annotation/model/consts";
import { PlusIcon, TrashIcon } from "@radix-ui/react-icons";
import {
  Box,
  Button,
  Flex,
  IconButton,
  ScrollArea,
  Spinner,
  Text,
} from "@radix-ui/themes";
import { toast } from "react-hot-toast";
import { Panel } from "react-resizable-panels";
import { twMerge } from "tailwind-merge";
import { useTimelineData } from "../hooks/useTimelineData";
import { usePlayer } from "@tezzro/player";

export function Journeys() {
  const selectedOrder = useAppSelector(
    (state) => state.annotation.selectedOrder
  );
  const journeyList = useAppSelector((state) => selectActiveJourneys(state));
  const annotationView = useAppSelector(selectAnnotationView);
  const annotationData = useAppSelector(selectAnnotationData);
  const isOrderLoading = useAppSelector(selectIsOrderLoading);
  const events = useAppSelector(selectEvents);
  const journeyTransactions = useAppSelector(selectJourneyTransactions);
  const trackPersistences = useAppSelector(selectTrackPersistences);
  const journeyProximities = useAppSelector(selectJourneyProximities);

  const selectedJourneyId = useAppSelector(
    (state) => state.annotation.selectedJourneyId
  );
  const allJourneyEvents = useAppSelector((state) => state.annotation.events);

  const dispatch = useAppDispatch();
  const player = usePlayer();
  const { removeTimelineItemsByGroup, removeTimelineGroup } = useTimelineData();
  const isEditable = useAppSelector(selectIsEditable);

  const canBeDeleted = (journey: Journey) => {
    if (annotationView !== ANNOTATION_TYPES.JOURNEY) {
      return false;
    }

    const isActiveRecord = (item: { status?: string }) =>
      item.status !== ANNOTATION_RECORD_STATUSES.DELETED;

    const hasActiveRecords = [
      events.some((item) => item.journeyId === journey.id),
      journeyTransactions.some(
        (item) => item.journeyId === journey.id && isActiveRecord(item)
      ),
      trackPersistences.some(
        (item) => item.journeyId === journey.id && isActiveRecord(item)
      ),
      journeyProximities.some(
        (item) => item.journeyId === journey.id && isActiveRecord(item)
      ),
    ].some(Boolean);

    return !hasActiveRecords;
  };

  const onDelete = (journey: Journey) => (mouseEvent: React.MouseEvent) => {
    mouseEvent.stopPropagation();

    dispatch(deleteJourney(journey));

    dispatch(setSelectedJourneyId(null));
    dispatch(setSelectedEvent(null));
    toast.success("Journey deleted successfully");
    removeTimelineGroup(journey.id);
    removeTimelineGroup(`${journey.id}-${ANNOTATION_TYPES.JOURNEY}`);
    removeTimelineItemsByGroup(journey.id);
  };

  const onSelectJourney = (journeyId: string) => async () => {
    if (annotationData) {
      toast.error("Complete the current annotation before proceeding");
      return;
    }

    if (selectedJourneyId !== journeyId) {
      dispatch(setSelectedJourneyId(journeyId));
      dispatch(setSelectedEvent(null));

      const journeyEvents = allJourneyEvents.filter(
        (event) => event.journeyId === journeyId
      );

      const enterEvent = journeyEvents.find(
        (event) => event.eventType === "enter"
      );

      if (!enterEvent) {
        return;
      }

      player.mediaPlayer.pause();
    }
  };

  const onAddJourney = () => {
    if (annotationData) {
      toast.error("Complete the current annotation before proceeding");
      return;
    }

    const customId = crypto.randomUUID();
    dispatch(addJourney(customId));
    dispatch(setSelectedJourneyId(customId));
  };

  const renderLoadingState = () => (
    <Flex justify="center" align="center">
      <Spinner size="1" />
      <Text ml="2" size="1">
        Loading journeys...
      </Text>
    </Flex>
  );

  const renderJourneyItem = (journey: Journey) => {
    const showDeleteButton = canBeDeleted(journey);

    return (
      <Flex
        justify="between"
        align="center"
        key={journey.id}
        className={twMerge([
          "p-2 hover:bg-neutral-200 hover:cursor-pointer",
          selectedJourneyId === journey.id &&
            "bg-neutral-200 hover:bg-neutral-200 ",
        ])}
        onClick={onSelectJourney(journey.id)}
      >
        <Text size="1">{journey.id}</Text>
        {isEditable &&
          annotationView === ANNOTATION_TYPES.JOURNEY &&
          showDeleteButton && (
            <IconButton
              size="1"
              color="gray"
              variant="soft"
              onClick={onDelete(journey)}
            >
              <TrashIcon />
            </IconButton>
          )}
      </Flex>
    );
  };

  const renderEmptyState = () => {
    if (selectedOrder) {
      return (
        <Flex justify="center" align="center" p="4">
          <Text size="1" color="gray">
            No journeys available for this order
          </Text>
        </Flex>
      );
    }
    return null;
  };

  const renderContent = () => {
    if (isOrderLoading) {
      return renderLoadingState();
    }

    if (journeyList.length > 0) {
      return journeyList.map(renderJourneyItem);
    }

    return renderEmptyState();
  };

  return (
    <Panel id="journeys" defaultSize={50}>
      <Box className="h-full">
        <Flex justify="between" align="center">
          <Text size="1" weight="bold" as="p" m="2">
            Journeys
          </Text>
          {selectedOrder &&
            isEditable &&
            annotationView === ANNOTATION_TYPES.JOURNEY && (
              <Button
                variant="ghost"
                color="gray"
                mr="2"
                onClick={onAddJourney}
              >
                <PlusIcon /> Add journey
              </Button>
            )}
        </Flex>

        <ScrollArea className="h-[calc(100%-40px)]">
          <Flex direction="column">{renderContent()}</Flex>
        </ScrollArea>
      </Box>
    </Panel>
  );
}
